import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  BookOpen,
  FileText,
  Plus,
  ArrowRight,
  DollarSign,
  TrendingUp,
  Loader2,
  AlertCircle,
  Settings,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Empresa {
  id: number;
  profileName: string;
  legalName: string;
  rfc: string;
  isDefault: boolean;
}

interface FinanceStats {
  totalAccounts: number;
  totalTransactions: number;
  totalPosted: number;
  totalDraft: number;
}

async function fetchEmpresas(): Promise<Empresa[]> {
  const res = await fetch('/api/finance/emisores', { credentials: 'include' });
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

async function fetchFinanceStats(empresaId?: number): Promise<FinanceStats> {
  const params = new URLSearchParams();
  if (empresaId) params.set('empresaId', String(empresaId));
  params.set('limit', '1');

  const [accountsRes, txPostedRes, txDraftRes] = await Promise.all([
    fetch(`/api/finance/accounts${empresaId ? `?empresaId=${empresaId}` : ''}`, { credentials: 'include' }),
    fetch(`/api/finance/transactions?status=posted&limit=1${empresaId ? `&empresaId=${empresaId}` : ''}`, { credentials: 'include' }),
    fetch(`/api/finance/transactions?status=draft&limit=1${empresaId ? `&empresaId=${empresaId}` : ''}`, { credentials: 'include' }),
  ]);

  let totalAccounts = 0;
  let totalPosted = 0;
  let totalDraft = 0;

  if (accountsRes.ok) {
    const data = await accountsRes.json();
    totalAccounts = Array.isArray(data) ? data.length : 0;
  }
  if (txPostedRes.ok) {
    const data = await txPostedRes.json();
    totalPosted = data?.total ?? (Array.isArray(data?.data) ? data.data.length : 0);
  }
  if (txDraftRes.ok) {
    const data = await txDraftRes.json();
    totalDraft = data?.total ?? (Array.isArray(data?.data) ? data.data.length : 0);
  }

  return { totalAccounts, totalTransactions: totalPosted + totalDraft, totalPosted, totalDraft };
}

const EMPRESA_COLORS = [
  { bg: 'bg-indigo-50', border: 'border-indigo-200', icon: 'bg-indigo-100 text-indigo-700', badge: 'bg-indigo-600' },
  { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'bg-emerald-100 text-emerald-700', badge: 'bg-emerald-600' },
  { bg: 'bg-violet-50', border: 'border-violet-200', icon: 'bg-violet-100 text-violet-700', badge: 'bg-violet-600' },
  { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'bg-amber-100 text-amber-700', badge: 'bg-amber-600' },
  { bg: 'bg-rose-50', border: 'border-rose-200', icon: 'bg-rose-100 text-rose-700', badge: 'bg-rose-600' },
  { bg: 'bg-cyan-50', border: 'border-cyan-200', icon: 'bg-cyan-100 text-cyan-700', badge: 'bg-cyan-600' },
];

function EmpresaCard({ empresa, colorIndex }: { empresa: Empresa; colorIndex: number }) {
  const navigate = useNavigate();
  const colors = EMPRESA_COLORS[colorIndex % EMPRESA_COLORS.length];

  const { data: stats, isLoading } = useQuery({
    queryKey: ['finance-stats', empresa.id],
    queryFn: () => fetchFinanceStats(empresa.id),
    staleTime: 60_000,
  });

  return (
    <div className={`rounded-xl border-2 ${colors.border} ${colors.bg} p-5 flex flex-col gap-4 hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-lg ${colors.icon}`}>
            <Building2 className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 leading-tight">{empresa.legalName}</h3>
            {empresa.rfc && (
              <p className="text-xs text-gray-500 font-mono mt-0.5">{empresa.rfc}</p>
            )}
            {empresa.profileName && empresa.profileName !== 'Principal' && (
              <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{empresa.profileName}</p>
            )}
          </div>
        </div>
        <span className={`text-xs text-white px-2 py-0.5 rounded-full font-medium ${colors.badge}`}>
          {empresa.isDefault ? 'Principal' : 'Activa'}
        </span>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <Loader2 className="w-3 h-3 animate-spin" />
          Cargando stats…
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-white/70 rounded-lg p-2">
            <p className="text-lg font-bold text-gray-800">{stats?.totalAccounts ?? 0}</p>
            <p className="text-xs text-gray-500">Cuentas</p>
          </div>
          <div className="bg-white/70 rounded-lg p-2">
            <p className="text-lg font-bold text-green-700">{stats?.totalPosted ?? 0}</p>
            <p className="text-xs text-gray-500">Contabilizadas</p>
          </div>
          <div className="bg-white/70 rounded-lg p-2">
            <p className="text-lg font-bold text-yellow-700">{stats?.totalDraft ?? 0}</p>
            <p className="text-xs text-gray-500">Borrador</p>
          </div>
        </div>
      )}

      <div className="flex gap-2 mt-auto">
        <button
          onClick={() => navigate(`/app/finance/chart-of-accounts?empresa=${empresa.id}`)}
          className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 rounded-lg py-2 transition-colors"
        >
          <BookOpen className="w-3.5 h-3.5" />
          Catálogo
          <ArrowRight className="w-3 h-3 ml-auto" />
        </button>
        <button
          onClick={() => navigate(`/app/finance/transactions?empresa=${empresa.id}`)}
          className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 rounded-lg py-2 transition-colors"
        >
          <FileText className="w-3.5 h-3.5" />
          Pólizas
          <ArrowRight className="w-3 h-3 ml-auto" />
        </button>
      </div>
    </div>
  );
}

function GeneralCard() {
  const navigate = useNavigate();

  const { data: stats, isLoading } = useQuery({
    queryKey: ['finance-stats', 'general'],
    queryFn: () => fetchFinanceStats(),
    staleTime: 60_000,
  });

  return (
    <div className="rounded-xl border-2 border-gray-300 bg-gray-50 p-5 flex flex-col gap-4 hover:shadow-md transition-shadow col-span-full sm:col-span-1">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-gray-200 text-gray-700">
            <DollarSign className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Vista General</h3>
            <p className="text-xs text-gray-500">Todos los libros contables</p>
          </div>
        </div>
        <span className="text-xs text-white px-2 py-0.5 rounded-full font-medium bg-gray-600">
          Global
        </span>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <Loader2 className="w-3 h-3 animate-spin" />
          Cargando stats…
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-white/70 rounded-lg p-2">
            <p className="text-lg font-bold text-gray-800">{stats?.totalAccounts ?? 0}</p>
            <p className="text-xs text-gray-500">Cuentas</p>
          </div>
          <div className="bg-white/70 rounded-lg p-2">
            <p className="text-lg font-bold text-green-700">{stats?.totalPosted ?? 0}</p>
            <p className="text-xs text-gray-500">Contabilizadas</p>
          </div>
          <div className="bg-white/70 rounded-lg p-2">
            <p className="text-lg font-bold text-yellow-700">{stats?.totalDraft ?? 0}</p>
            <p className="text-xs text-gray-500">Borrador</p>
          </div>
        </div>
      )}

      <div className="flex gap-2 mt-auto">
        <button
          onClick={() => navigate('/app/finance/chart-of-accounts')}
          className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 rounded-lg py-2 transition-colors"
        >
          <BookOpen className="w-3.5 h-3.5" />
          Catálogo
          <ArrowRight className="w-3 h-3 ml-auto" />
        </button>
        <button
          onClick={() => navigate('/app/finance/transactions')}
          className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 rounded-lg py-2 transition-colors"
        >
          <FileText className="w-3.5 h-3.5" />
          Pólizas
          <ArrowRight className="w-3 h-3 ml-auto" />
        </button>
      </div>
    </div>
  );
}

export default function FinanceDashboard() {
  const navigate = useNavigate();

  const { data: empresas = [], isLoading, error } = useQuery({
    queryKey: ['empresas'],
    queryFn: fetchEmpresas,
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-7 h-7 text-indigo-600" />
              Empresas (Razones Sociales)
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Cada perfil de Datos Fiscales representa una empresa contable. Selecciona una para ver su catálogo y pólizas.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/app/admin?tab=facturacion')}
              className="gap-2"
            >
              <Settings className="w-4 h-4" />
              Gestionar Datos Fiscales
            </Button>
            <Button
              onClick={() => navigate('/app/finance/new-entry')}
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              Nueva Póliza
            </Button>
          </div>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
          </div>
        )}

        {error && (
          <div className="flex items-center gap-3 text-red-600 bg-red-50 border border-red-200 rounded-lg p-4">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <p>Error al cargar las empresas</p>
          </div>
        )}

        {!isLoading && !error && (
          <>
            {/* Empresa Layers Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <GeneralCard />
              {empresas.map((emp, idx) => (
                <EmpresaCard key={emp.id} empresa={emp} colorIndex={idx} />
              ))}

            </div>
            {empresas.length === 0 && (
              <div className="col-span-full flex flex-col items-center justify-center py-16 gap-4">
                <Building2 className="w-12 h-12 text-gray-300" />
                <div className="text-center">
                  <p className="text-gray-500 font-medium">Sin razones sociales configuradas</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Los perfiles de Datos Fiscales del Emisor aparecen aquí automáticamente.
                  </p>
                </div>
                <Button variant="outline" onClick={() => navigate('/app/admin?tab=facturacion')} className="gap-2">
                  <Settings className="w-4 h-4" />
                  Ir a Datos Fiscales del Emisor
                </Button>
              </div>
            )}

            {/* Quick Actions */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <button
                onClick={() => navigate('/app/finance/chart-of-accounts')}
                className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4 hover:border-indigo-300 hover:shadow-sm transition-all text-left"
              >
                <div className="p-2.5 rounded-lg bg-blue-50 text-blue-600">
                  <BookOpen className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Catálogo de Cuentas</p>
                  <p className="text-xs text-gray-500">Ver y gestionar el plan contable</p>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 ml-auto" />
              </button>

              <button
                onClick={() => navigate('/app/finance/transactions')}
                className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4 hover:border-indigo-300 hover:shadow-sm transition-all text-left"
              >
                <div className="p-2.5 rounded-lg bg-green-50 text-green-600">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Pólizas Contables</p>
                  <p className="text-xs text-gray-500">Historial de movimientos</p>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 ml-auto" />
              </button>

              <button
                onClick={() => navigate('/app/finance/new-entry')}
                className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4 hover:border-indigo-300 hover:shadow-sm transition-all text-left"
              >
                <div className="p-2.5 rounded-lg bg-purple-50 text-purple-600">
                  <Plus className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Nueva Póliza</p>
                  <p className="text-xs text-gray-500">Registrar asiento manual</p>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 ml-auto" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
